import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  ArrowLeft, Edit2, Mail, Phone, Globe, MapPin, Truck, ClipboardList, Package, Star,
  Archive, ArchiveRestore, Printer, ShieldCheck, ShieldAlert, AlertTriangle, ChevronDown, ChevronRight,
  Plus, Trash2, FileText, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import EmailPurchaserDialog from '../components/EmailPurchaserDialog';
import { CurrencySelect } from '../components/CurrencySelect';

const Field = ({ icon: Icon, label, children, testId }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    {Icon && <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />}
    <div className="min-w-0 flex-1">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 break-words">{children}</div>
    </div>
  </div>
);

const Section = ({ id, vendorId, title, defaultOpen = false, headerExtra, children }) => {
  const storageKey = `vendorSection:${vendorId}:${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const v = window.localStorage.getItem(storageKey);
      return v === null ? defaultOpen : v === '1';
    } catch { return defaultOpen; }
  });
  const toggle = () => {
    setOpen(o => {
      try { window.localStorage.setItem(storageKey, !o ? '1' : '0'); } catch {}
      return !o;
    });
  };
  return (
    <div className="industrial-card border border-slate-200 rounded-sm bg-white" data-testid={`vendor-section-${id}`} data-section-open={open}>
      <button type="button" onClick={toggle} className="w-full px-4 py-3 flex items-center justify-between border-b border-slate-100 hover:bg-slate-50" data-testid={`vendor-section-toggle-${id}`}>
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          {title}
        </div>
        {headerExtra && <div onClick={e => e.stopPropagation()}>{headerExtra}</div>}
      </button>
      {open && <div className="p-0">{children}</div>}
    </div>
  );
};

const StarRating = ({ value, onChange, readOnly = false, size = 'md' }) => {
  const [hover, setHover] = useState(0);
  const cls = size === 'lg' ? 'w-6 h-6' : 'w-4 h-4';
  return (
    <div className="flex items-center gap-0.5" data-testid="vendor-star-rating">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = (hover || value || 0) >= n;
        return (
          <button
            key={n}
            type="button"
            disabled={readOnly}
            onClick={() => !readOnly && onChange?.(n === value ? null : n)}
            onMouseEnter={() => !readOnly && setHover(n)}
            onMouseLeave={() => !readOnly && setHover(0)}
            className={readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110 transition-transform'}
            data-testid={`vendor-star-${n}`}
          >
            <Star className={`${cls} ${filled ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
          </button>
        );
      })}
      {!readOnly && value && (
        <button type="button" onClick={() => onChange?.(null)} className="text-[10px] uppercase text-slate-400 ml-1 hover:text-slate-700">clear</button>
      )}
    </div>
  );
};

const VendorDetailPage = () => {
  const { id } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [noteText, setNoteText] = useState('');
  const [purchaserOpen, setPurchaserOpen] = useState(false);
  const [purchaserCtx, setPurchaserCtx] = useState({ type: 'vendor_order', item: null });

  const fetchCard = async () => {
    setLoading(true);
    try {
      const r = await api().get(`/vendors/${id}/full-card`);
      setCard(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load vendor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCard(); }, [id]);

  const v = card?.vendor;

  const startEdit = () => {
    setForm({
      vendor_name: v.vendor_name || '',
      contact_name: v.contact_name || '',
      contact_email: v.contact_email || '',
      contact_phone: v.contact_phone || '',
      contact_info: v.contact_info || '',
      website: v.website || '',
      payment_terms: v.payment_terms || '',
      gst_number: v.gst_number || '',
      default_currency: v.default_currency || 'CAD',
      min_order_value: v.min_order_value ?? '',
      ordering_notes: v.ordering_notes || '',
      lead_time_days: v.lead_time_days || 0,
      address_line1: v.address_line1 || '',
      address_line2: v.address_line2 || '',
      city: v.city || '',
      state_province: v.state_province || '',
      country: v.country || '',
      postal_code: v.postal_code || '',
      active_status: v.active_status !== false,
      delay_risk: v.delay_risk || 1,
      cost_risk: v.cost_risk || 1,
      geopolitical_risk: v.geopolitical_risk || 1,
      risk_notes: v.risk_notes || '',
      star_rating: v.star_rating ?? null,
      star_rating_notes: v.star_rating_notes || '',
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      const payload = {
        ...form,
        lead_time_days: parseInt(form.lead_time_days) || 0,
        delay_risk: parseInt(form.delay_risk) || 1,
        cost_risk: parseInt(form.cost_risk) || 1,
        geopolitical_risk: parseInt(form.geopolitical_risk) || 1,
        star_rating: form.star_rating ? parseInt(form.star_rating) : null,
        min_order_value: form.min_order_value === '' || form.min_order_value === null ? null : parseFloat(form.min_order_value),
      };
      await api().put(`/vendors/${id}`, payload);
      toast.success('Vendor updated');
      setEditOpen(false);
      fetchCard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const archive = async () => {
    if (!window.confirm(`Archive ${v.vendor_name}? Vendor history will be preserved. You can unarchive later.`)) return;
    try {
      await api().delete(`/vendors/${id}`);
      toast.success('Vendor archived');
      fetchCard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Archive failed');
    }
  };

  const unarchive = async () => {
    try {
      await api().post(`/vendors/${id}/unarchive`);
      toast.success('Vendor unarchived');
      fetchCard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unarchive failed');
    }
  };

  const acknowledgeReview = async () => {
    try {
      await api().post(`/vendors/${id}/acknowledge-risk-review`);
      toast.success('Risk review acknowledged');
      fetchCard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const updateStarInline = async (newRating) => {
    try {
      await api().put(`/vendors/${id}`, {
        ...v,
        star_rating: newRating,
      });
      toast.success(newRating ? `Rated ${newRating} star${newRating > 1 ? 's' : ''}` : 'Rating cleared');
      fetchCard();
    } catch (e) {
      toast.error('Failed to update rating');
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await api().post(`/vendors/${id}/notes`, { content: noteText.trim() });
      setNoteText('');
      fetchCard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add note');
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await api().delete(`/vendors/${id}/notes/${noteId}`);
      fetchCard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading || !card) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  const fullAddress = [v.address_line1, v.address_line2, v.city, v.state_province, v.postal_code, v.country].filter(Boolean).join(', ');
  const itemsLow = (card.items_supplied || []).filter(i => (i.current_quantity || 0) <= (i.reorder_point || 0) && (i.reorder_point || 0) > 0);

  return (
    <div className="space-y-4 animate-slideIn pb-12" data-testid="vendor-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/vendors')} data-testid="vendor-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> All Vendors
        </Button>
        <div className="flex gap-2">
          {v.archived ? (
            <Button variant="outline" size="sm" onClick={unarchive} data-testid="vendor-unarchive-btn">
              <ArchiveRestore className="w-4 h-4 mr-1" /> Unarchive
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={archive} className="text-red-600 hover:bg-red-50" data-testid="vendor-archive-btn">
              <Archive className="w-4 h-4 mr-1" /> Archive
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={startEdit} data-testid="vendor-edit-btn">
            <Edit2 className="w-4 h-4 mr-1" /> Edit
          </Button>
        </div>
      </div>

      {/* Risk Review Nudge */}
      {v.risk_review_required && (
        <div className="bg-amber-50 border border-amber-300 rounded-sm p-4 flex items-start gap-3" data-testid="vendor-risk-review-nudge">
          <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-amber-800">Risk Review Pending</h3>
            <p className="text-amber-700 text-sm">This vendor was recently created. Please review the risk indices (Delay, Cost, Geopolitical) and add risk notes before sourcing decisions.</p>
          </div>
          <Button size="sm" variant="outline" onClick={acknowledgeReview} className="border-amber-300 text-amber-700 hover:bg-amber-100" data-testid="vendor-ack-review-btn">
            <ShieldCheck className="w-4 h-4 mr-1" /> Acknowledge
          </Button>
        </div>
      )}

      {/* Hero Info Card (Item Master parity) */}
      <Card className="industrial-card border border-slate-200">
        <CardContent className="p-5">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-16 h-16 rounded-sm bg-orange-100 flex items-center justify-center flex-shrink-0">
              <Truck className="w-8 h-8 text-orange-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-slate-900" data-testid="vendor-name-heading">{v.vendor_name}</h1>
                <span className={`status-badge ${v.archived ? 'status-obsolete' : v.active_status ? 'status-released' : 'status-obsolete'}`} data-testid="vendor-status-badge">
                  {v.archived ? 'Archived' : v.active_status ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-wider text-slate-500">Star Rating</span>
                  <StarRating value={v.star_rating} onChange={updateStarInline} size="lg" />
                </div>
                {v.star_rating_notes && (
                  <span className="text-xs text-slate-500 italic">"{v.star_rating_notes}"</span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-5 pt-4 border-t border-slate-100">
            <Field icon={Mail} label="Contact" testId="vendor-field-contact">
              <div className="font-medium">{v.contact_name || '—'}</div>
              {v.contact_email && (
                <a href={`mailto:${v.contact_email}`} className="text-xs text-orange-600 hover:underline mono break-all">{v.contact_email}</a>
              )}
            </Field>
            <Field icon={Phone} label="Phone" testId="vendor-field-phone">
              {v.contact_phone ? <a href={`tel:${v.contact_phone}`} className="text-orange-600 hover:underline mono">{v.contact_phone}</a> : (v.contact_info || '—')}
            </Field>
            <Field icon={Globe} label="Website" testId="vendor-field-website">
              {v.website ? (
                <a href={v.website} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline mono break-all flex items-center gap-1">
                  {v.website.replace(/^https?:\/\//, '').slice(0, 32)} <ExternalLink className="w-3 h-3" />
                </a>
              ) : '—'}
            </Field>
            <Field icon={MapPin} label="Address" testId="vendor-field-address">
              {fullAddress || '—'}
            </Field>
            <Field icon={ClipboardList} label="Payment Terms" testId="vendor-field-payment">{v.payment_terms || '—'}</Field>
            <Field icon={ClipboardList} label="GST / HST Reg #" testId="vendor-field-gst"><span className="mono">{v.gst_number || '—'}</span></Field>
            <Field icon={ClipboardList} label="Ordering" testId="vendor-field-ordering"><span className="mono">{v.default_currency || 'CAD'}</span>{v.min_order_value ? ` · min order ${v.min_order_value}` : ''}{v.ordering_notes && <div className="text-xs text-slate-500 mt-0.5">{v.ordering_notes}</div>}</Field>
            <Field icon={Truck} label="Lead Time" testId="vendor-field-leadtime"><span className="mono">{v.lead_time_days || 0} days</span></Field>
            <Field icon={Package} label="Items Supplied" testId="vendor-field-items"><span className="mono font-semibold">{card.items_supplied_count}</span> SKUs</Field>
            <Field icon={AlertTriangle} label="Low Stock" testId="vendor-field-lowstock">
              <span className={`mono font-semibold ${itemsLow.length > 0 ? 'text-red-600' : 'text-emerald-700'}`}>{itemsLow.length}</span> items
            </Field>
          </div>

          {/* Risk Indices */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            {[
              { key: 'delay_risk', label: 'Delay Risk' },
              { key: 'cost_risk', label: 'Cost Volatility' },
              { key: 'geopolitical_risk', label: 'Geopolitical' },
            ].map(({ key, label }) => {
              const val = v[key] || 1;
              const color = val >= 4 ? 'bg-red-50 text-red-700 border-red-200' : val === 3 ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200';
              return (
                <div key={key} className={`border rounded-sm p-3 ${color}`} data-testid={`vendor-risk-${key.replace('_', '-')}`}>
                  <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
                  <div className="flex items-baseline gap-1 mt-0.5">
                    <span className="text-2xl font-bold mono">{val}</span>
                    <span className="text-xs opacity-60">/ 5</span>
                  </div>
                </div>
              );
            })}
          </div>

          {v.risk_notes && (
            <div className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-2" data-testid="vendor-risk-notes-display">
              <span className="font-semibold">Risk Notes:</span> {v.risk_notes}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Items Supplied */}
      <Section id="items" vendorId={id} title={`Items Supplied (${card.items_supplied_count})`} defaultOpen>
        {card.items_supplied.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">No items source from this vendor yet.</div>
        ) : (
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>SKU</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Country</TableHead>
                <TableHead className="text-right">On Hand</TableHead>
                <TableHead className="text-right">Reorder Pt</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {card.items_supplied.map(it => {
                const low = (it.current_quantity || 0) <= (it.reorder_point || 0) && (it.reorder_point || 0) > 0;
                return (
                  <TableRow key={it.id} className={low ? 'bg-red-50' : ''} data-testid={`vendor-item-row-${it.id}`}>
                    <TableCell className="mono"><a className="text-orange-600 hover:underline" onClick={() => navigate(`/items/view/${it.id}`)} role="button">{it.item_sku}</a></TableCell>
                    <TableCell>{it.item_name}</TableCell>
                    <TableCell className="text-xs text-slate-500">{it.country_of_origin || '—'}</TableCell>
                    <TableCell className="text-right mono">{it.current_quantity}</TableCell>
                    <TableCell className="text-right mono">{it.reorder_point}</TableCell>
                    <TableCell>{low ? <span className="text-red-600 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Low</span> : <span className="text-emerald-700 text-xs">In Stock</span>}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Section>

      {/* Pending Orders */}
      <Section id="pending" vendorId={id} title={`Pending / Expected Orders (${(card.pending_orders || []).length})`}>
        {(card.pending_orders || []).length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">No open expected orders.</div>
        ) : (
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>PO #</TableHead>
                <TableHead>Item</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead>ETA</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {card.pending_orders.map(eo => (
                <TableRow key={eo.id} data-testid={`vendor-pending-${eo.id}`}>
                  <TableCell className="mono">{eo.purchase_order_number || '—'}</TableCell>
                  <TableCell>{eo.item_name}</TableCell>
                  <TableCell className="text-right mono">{eo.expected_quantity}</TableCell>
                  <TableCell className="text-xs text-slate-500">{eo.expected_arrival_date ? new Date(eo.expected_arrival_date).toLocaleDateString() : '—'}</TableCell>
                  <TableCell className="text-xs text-slate-600 truncate max-w-[200px]">{eo.notes || ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      {/* Recent Restock Sessions */}
      <Section id="restock" vendorId={id} title={`Recent Receipts (${(card.recent_sessions || []).length})`}>
        {(card.recent_sessions || []).length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">No restock sessions yet.</div>
        ) : (
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Lot</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead>Carrier</TableHead>
                <TableHead className="text-right">Landed $</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {card.recent_sessions.map(s => (
                <TableRow key={s.id} data-testid={`vendor-restock-${s.id}`}>
                  <TableCell className="text-xs">{s.received_at ? new Date(s.received_at).toLocaleDateString() : '—'}</TableCell>
                  <TableCell>{s.item_name}</TableCell>
                  <TableCell className="mono text-xs">{s.batch_lot || '—'}</TableCell>
                  <TableCell className="text-right mono">{s.quantity}</TableCell>
                  <TableCell className="text-xs">{s.carrier === 'Other' ? (s.carrier_other || 'Other') : (s.carrier || '—')}</TableCell>
                  <TableCell className="text-right mono">{s.landed_unit_cost_usd ? `$${Number(s.landed_unit_cost_usd).toFixed(2)}` : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      {/* Notes */}
      <Section id="notes" vendorId={id} title={`Notes (${(card.notes || []).length})`}>
        <div className="p-4 space-y-3">
          <div className="flex gap-2">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a note about this vendor (e.g., onboarding status, MSA details)..."
              className="rounded-sm"
              rows={2}
              data-testid="vendor-note-input"
            />
            <Button onClick={addNote} disabled={!noteText.trim()} className="bg-slate-900 hover:bg-slate-800 self-start" data-testid="vendor-add-note-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
          {(card.notes || []).length === 0 ? (
            <div className="text-center text-sm text-slate-400 py-4">No notes yet.</div>
          ) : (
            <div className="space-y-2">
              {card.notes.map(n => (
                <div key={n.id} className="border border-slate-200 rounded-sm p-3 bg-slate-50" data-testid={`vendor-note-${n.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm text-slate-800 whitespace-pre-wrap flex-1">{n.content}</p>
                    <Button variant="ghost" size="sm" onClick={() => deleteNote(n.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 -mr-2" data-testid={`vendor-note-delete-${n.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">{n.author_name} · {n.created_at ? new Date(n.created_at).toLocaleString() : ''}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* Actions */}
      <div className="flex gap-2 flex-wrap" data-testid="vendor-actions">
        <Button variant="outline" onClick={() => { setPurchaserCtx({ type: 'vendor_order', item: null }); setPurchaserOpen(true); }} data-testid="vendor-action-email">
          <Mail className="w-4 h-4 mr-1" /> Email Purchaser
        </Button>
        <Button variant="outline" onClick={() => navigate(`/rfx?vendor=${id}`)} data-testid="vendor-action-rfx">
          <FileText className="w-4 h-4 mr-1" /> Create RFQ / RFP / RFI
        </Button>
      </div>

      {/* Edit Dialog */}
      {editOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditOpen(false)}>
          <div className="bg-white rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="vendor-edit-dialog">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Edit Vendor</h2>
              <Button variant="ghost" size="sm" onClick={() => setEditOpen(false)}>×</Button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Vendor Name *</Label><Input value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} className="rounded-sm" data-testid="vendor-edit-name" /></div>
                <div><Label>Contact Name</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="rounded-sm" /></div>
                <div><Label>Contact Email</Label><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="rounded-sm mono" data-testid="vendor-edit-email" /></div>
                <div><Label>Contact Phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="rounded-sm mono" /></div>
                <div><Label>Website</Label><Input type="url" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="rounded-sm mono" /></div>
                <div className="col-span-2"><Label>Other Contact Info</Label><Input value={form.contact_info} onChange={(e) => setForm({ ...form, contact_info: e.target.value })} className="rounded-sm" /></div>
              </div>
              <div className="border-t border-slate-200 pt-3">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Address</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Address Line 1</Label><Input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} className="rounded-sm" /></div>
                  <div className="col-span-2"><Label>Address Line 2</Label><Input value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} className="rounded-sm" /></div>
                  <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-sm" /></div>
                  <div><Label>State / Province</Label><Input value={form.state_province} onChange={(e) => setForm({ ...form, state_province: e.target.value })} className="rounded-sm" /></div>
                  <div><Label>Postal Code</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="rounded-sm mono" /></div>
                  <div><Label>Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="rounded-sm" /></div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 border-t border-slate-200 pt-3">
                <div><Label>Payment Terms</Label><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="rounded-sm" /></div>
                <div><Label>GST / HST Reg #</Label><Input value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} className="rounded-sm mono" data-testid="vendor-edit-gst" /></div>
                <div><Label>Default Currency</Label><CurrencySelect value={form.default_currency} onChange={(c) => setForm({ ...form, default_currency: c })} className="w-full" testId="vendor-edit-currency" /></div>
                <div><Label>Minimum Order Value</Label><Input type="number" min="0" step="0.01" value={form.min_order_value} onChange={(e) => setForm({ ...form, min_order_value: e.target.value })} className="rounded-sm mono" data-testid="vendor-edit-min-order" /></div>
                <div><Label>Ordering Notes</Label><Input value={form.ordering_notes} onChange={(e) => setForm({ ...form, ordering_notes: e.target.value })} placeholder="e.g. $500 minimum, free freight over $1,000" className="rounded-sm" data-testid="vendor-edit-ordering-notes" /></div>
                <div><Label>Lead Time (days)</Label><Input type="number" value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })} className="rounded-sm mono" /></div>
              </div>

              <div className="border-t border-slate-200 pt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Star Rating</Label>
                  <StarRating value={form.star_rating} onChange={(n) => setForm({ ...form, star_rating: n })} size="lg" />
                </div>
                <Input
                  placeholder="Why this rating? (optional)"
                  value={form.star_rating_notes}
                  onChange={(e) => setForm({ ...form, star_rating_notes: e.target.value })}
                  className="rounded-sm text-sm"
                  data-testid="vendor-edit-star-notes"
                />
              </div>

              <div className="border-t border-slate-200 pt-3 space-y-3">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Global Risk Index</div>
                {[
                  { key: 'delay_risk', label: 'Delay Risk' },
                  { key: 'cost_risk', label: 'Cost Volatility' },
                  { key: 'geopolitical_risk', label: 'Geopolitical / Trade' },
                ].map(({ key, label }) => (
                  <div key={key} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">{label}</Label>
                      <span className="text-xs mono text-slate-700 px-2 py-0.5 bg-slate-100 rounded-sm">{form[key]} / 5</span>
                    </div>
                    <input type="range" min={1} max={5} value={form[key]} onChange={(e) => setForm({ ...form, [key]: parseInt(e.target.value) })} className="w-full accent-orange-500" />
                  </div>
                ))}
                <Textarea value={form.risk_notes} onChange={(e) => setForm({ ...form, risk_notes: e.target.value })} placeholder="Risk notes..." className="rounded-sm" rows={2} />
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button onClick={saveEdit} className="bg-slate-900 hover:bg-slate-800" data-testid="vendor-edit-save-btn">Save</Button>
            </div>
          </div>
        </div>
      )}

      <EmailPurchaserDialog
        open={purchaserOpen}
        onOpenChange={setPurchaserOpen}
        requestType={purchaserCtx.type}
        item={purchaserCtx.item}
        vendor={v}
      />
    </div>
  );
};

export default VendorDetailPage;
